import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Activity, ChevronUp, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only fixed-corner version banner.
 * Shows the git SHA + boot time so super-admins can visually confirm a fresh
 * deploy reached the environment they're looking at. Hidden for non-admins.
 *
 * Calls /api/deployment-info (public endpoint) on mount + every 60s.
 */
export default function AdminVersionBanner() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem('admin_version_banner_dismissed') === '1'
  );

  const isAdmin = user && ['admin', 'super_admin', 'org_admin'].includes(user.role);

  useEffect(() => {
    if (!isAdmin || dismissed) return;
    let cancelled = false;
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${API}/deployment-info`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setInfo(data);
      } catch {
        /* ignore */
      }
    };
    fetchInfo();
    const t = setInterval(fetchInfo, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAdmin, dismissed]);

  if (!isAdmin || dismissed || !info) return null;

  const commit = info.git_commit || 'unknown';
  const commitTime = info.git_last_commit_time
    ? new Date(info.git_last_commit_time).toLocaleString()
    : '-';
  const bootTime = info.boot_time
    ? new Date(info.boot_time).toLocaleString()
    : '-';

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem('admin_version_banner_dismissed', '1');
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] select-none"
      data-testid="admin-version-banner"
    >
      <div
        className={`bg-slate-900/95 backdrop-blur-md border border-emerald-500/40 rounded-lg shadow-2xl transition-all ${
          expanded ? 'w-80' : 'w-auto'
        }`}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-800/50 rounded-lg"
          data-testid="version-banner-toggle"
        >
          <Activity className="w-3.5 h-3.5 text-emerald-400" />
          <code className="text-xs font-mono text-emerald-300">
            {commit}
          </code>
          {!expanded && (
            <span className="text-[10px] text-slate-300 ml-1">
              {info.git_branch || ''}
            </span>
          )}
          <ChevronUp
            className={`w-3 h-3 text-slate-300 transition-transform ${expanded ? '' : 'rotate-180'}`}
          />
          <button
            onClick={handleDismiss}
            className="ml-1 text-slate-300 hover:text-slate-300"
            title="Hide for this session"
            data-testid="version-banner-dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        </button>

        {expanded && (
          <div className="px-3 pb-3 pt-1 space-y-1 text-xs border-t border-slate-800">
            <Row label="Branch" value={info.git_branch || '-'} />
            <Row label="Last commit" value={commitTime} />
            <Row label="Boot time" value={bootTime} />
            <Row label="DB" value={info.db_name || '-'} />
            <Row label="Seed mode" value={
              <span className="text-emerald-300">{info.seed_mode || '-'}</span>
            } />
            {info.last_data_refresh ? (
              <Row label="Last refresh" value={
                new Date(info.last_data_refresh.started_at).toLocaleString()
              } />
            ) : (
              <Row label="Last refresh" value={
                <span className="text-amber-300">never</span>
              } />
            )}
            <Link
              to="/admin/deployment-status"
              className="block mt-2 text-center text-[11px] text-blue-300 hover:text-blue-200 underline"
              data-testid="version-banner-open-status"
            >
              Open Deployment Status →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-300">{label}</span>
      <span className="text-slate-200 truncate max-w-[180px]">{value}</span>
    </div>
  );
}
